import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { Card } from '../ui/card';

export const RefineryPayArticle = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "Why is shutdown pay so unpredictable?",
      answer: "Dynamic schedules, multiple pay rates, OT spikes, and per diem variations change constantly during shutdowns, making traditional payroll tracking inadequate."
    },
    {
      question: "What makes refinery pay different from other construction work?",
      answer: "Refinery shutdowns involve rapid schedule changes, multiple departments with different rates, sudden staffing needs, and complex per diem rules that vary daily."
    },
    {
      question: "How can workers track their pay during shutdowns?",
      answer: "PayFlow provides real-time tracking of hours, rates, OT rules, and per diem specific to shutdown work, updating paycheck estimates as conditions change."
    },
    {
      question: "Do shutdown workers need different tools than regular construction workers?",
      answer: "While the challenges are more intense, both need real-time pay visibility. PayFlow handles the added complexity of shutdowns while remaining simple to use."
    },
    {
      question: "Can PayFlow handle multiple projects during shutdowns?",
      answer: "Yes. PayFlow supports multiple projects with different rates, OT rules, and per diem settings, tracking everything in real time for accurate pay estimates."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Hidden Reasons Shutdown and Refinery Pay Is So Hard to Predict",
    "description": "An analysis of why shutdown and refinery pay fluctuates and how PayFlow gives workers accurate, real-time visibility into their earnings.",
    "author": {
      "@type": "Organization",
      "name": "ProjectFlow Automations"
    },
    "keywords": "shutdown pay, refinery payroll, turnaround overtime, per diem errors, PayFlow app",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://projectflowautomations.com/knowledge/refinery-pay-issues"
    }
  };

  return (
    <>
      <Helmet>
        <title>Why Shutdown & Refinery Pay Is Hard to Predict | PayFlow</title>
        <meta 
          name="description" 
          content="Explore why shutdown and refinery pay is unpredictable and how PayFlow provides real-time pay estimates for hours, OT, and per diem." 
        />
        <meta name="keywords" content="refinery payroll issues, shutdown pay prediction, turnaround overtime, construction pay estimator, PayFlow refinery workers" />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1115] pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="/knowledge"
            className="inline-flex items-center text-[#30D5C8] hover:text-[#30D5C8]/80 font-['Open_Sans'] mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Knowledge Center
          </a>

          <div className="mb-12">
            <div className="inline-block bg-[#30D5C8]/10 border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">
                REFINERY & SHUTDOWNS
              </span>
            </div>
            <h1 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6 leading-tight">
              The Hidden Reasons Shutdown and Refinery Pay Is So Hard to Predict
            </h1>
            <p className="text-[#CBD3D9] text-xl font-['Open_Sans'] leading-relaxed">
              Shutdowns and refinery turnarounds create some of the most unpredictable pay in any industry. Workers often cannot predict their checks because the variables change constantly.
            </p>
          </div>

          <article className="space-y-8">
            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                Why Shutdown Pay Is So Complicated
              </h2>
              
              <div className="space-y-6">
                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <AlertTriangle className="text-[#30D5C8] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                        1. Dynamic Schedules
                      </h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                        Shift times change rapidly \u2014 sometimes daily.
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <AlertTriangle className="text-[#30D5C8] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                        2. Multiple Pay Rates
                      </h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans'] mb-4">
                        Workers may switch:
                      </p>
                      <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Tasks</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Departments</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Codes</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Premium shifts</span>
                        </li>
                      </ul>
                      <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold mt-4">
                        All within the same week.
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <AlertTriangle className="text-[#30D5C8] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                        3. OT Spikes
                      </h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans'] mb-4">
                        Sudden staffing needs create:
                      </p>
                      <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Unexpected OT</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Double time</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#30D5C8] mr-3">\u2022</span>
                          <span>Extended shifts</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Card>

                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <AlertTriangle className="text-[#30D5C8] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                        4. Per Diem Variations
                      </h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                        Partial days, travel days, and miscommunication lead to errors.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                Why Workers Need Real-Time Pay Tools
              </h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#30D5C8]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">
                  Shutdown workers deserve tools that understand:
                </p>
                <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans'] text-lg">
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">\u2713</span>
                    <span>Their schedule</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">\u2713</span>
                    <span>Their rates</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">\u2713</span>
                    <span>Their OT rules</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">\u2713</span>
                    <span>Their per diem structure</span>
                  </li>
                </ul>
                <p className="text-[#F5F7F8] font-['Montserrat'] font-bold text-xl mt-6">
                  And update their paycheck totals in real time.
                </p>
              </Card>
            </section>

            <Card className="bg-gradient-to-br from-[#30D5C8]/20 to-[#30D5C8]/5 border-[#30D5C8]/50 p-8">
              <p className="text-[#F5F7F8] font-['Open_Sans'] text-lg leading-relaxed mb-6">
                PayFlow by ProjectFlow Automations is built for construction and refinery workers who want clarity on hours, overtime, and per diem before payday.
              </p>
              <a
                href="/payflow"
                className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors"
              >
                Learn More About PayFlow
              </a>
            </Card>
          </article>

          <section className="mt-16">
            <h2 className="font-['Montserrat'] font-bold text-3xl text-[#F5F7F8] mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6">
                  <h3 className="font-['Montserrat'] font-bold text-lg text-[#30D5C8] mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
